import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMe } from "../api/api_hooks";
import {
  createRemoteSession,
  endRemoteSession,
  reconnectRemoteSession,
  remoteSocketUrl,
  resetRemoteSession,
} from "./remoteApi";
import { CONTROLLER_RECONNECT_GRACE_MS, hardRefreshWebsite } from "./remoteActions";
import { t } from "../i18n";

const HOST_STORAGE_KEY = "pocket-play:website-remote-host";
const RemoteControlContext = React.createContext(null);

function readStoredHost() {
  try {
    return JSON.parse(window.sessionStorage.getItem(HOST_STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function storeHost(session) {
  if (session) window.sessionStorage.setItem(HOST_STORAGE_KEY, JSON.stringify(session));
  else window.sessionStorage.removeItem(HOST_STORAGE_KEY);
}

function messageId() {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function RemoteControlProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [session, setSession] = React.useState(null);
  const [status, setStatus] = React.useState("idle");
  const [controllerConnected, setControllerConnected] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [error, setError] = React.useState("");
  const socketRef = React.useRef(null);
  const reconnectTimerRef = React.useRef(null);
  const controllerReconnectTimerRef = React.useRef(null);
  const resetPairingRef = React.useRef(null);
  const closedRef = React.useRef(false);
  const pageRef = React.useRef(null);
  const sequenceRef = React.useRef(0);
  const sessionRef = React.useRef(session);
  const controllerConnectedRef = React.useRef(controllerConnected);

  sessionRef.current = session;
  controllerConnectedRef.current = controllerConnected;

  const globalActions = React.useMemo(() => ({
    "navigation.games": () => navigate("/"),
    "navigation.tournaments": () => navigate("/tournaments"),
    "navigation.plans": () => navigate("/subscriptions"),
    "navigation.about": () => navigate("/about"),
    "navigation.account": () => navigate("/account"),
    "navigation.login": () => navigate("/login"),
    "navigation.register": () => navigate("/register"),
    "navigation.back": () => navigate(-1),
    "viewport.up": () => window.scrollBy({ top: -Math.max(320, window.innerHeight * 0.75), behavior: "smooth" }),
    "viewport.down": () => window.scrollBy({ top: Math.max(320, window.innerHeight * 0.75), behavior: "smooth" }),
    "viewport.top": () => window.scrollTo({ top: 0, behavior: "smooth" }),
    "viewport.bottom": () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" }),
    "system.hardRefresh": hardRefreshWebsite,
  }), [navigate]);

  const navigationControls = React.useMemo(() => {
    const controls = [
      { id: "navigation.games", type: "button", label: t("Games") },
      ...(me ? [{ id: "navigation.tournaments", type: "button", label: t("Tournaments") }] : []),
      { id: "navigation.plans", type: "button", label: t("Plans") },
      { id: "navigation.about", type: "button", label: t("About") },
      ...(me
        ? [{ id: "navigation.account", type: "button", label: t("Account") }]
        : [
            { id: "navigation.login", type: "button", label: t("Log in") },
            { id: "navigation.register", type: "button", label: t("Sign up") },
          ]),
    ];
    return [
      { title: t("Navigate"), controls },
      {
        title: t("Page"),
        controls: [
          { id: "navigation.back", type: "button", label: t("Back") },
          { id: "viewport.up", type: "button", label: t("Scroll up") },
          { id: "viewport.down", type: "button", label: t("Scroll down") },
          { id: "viewport.top", type: "button", label: t("Top") },
          { id: "viewport.bottom", type: "button", label: t("Bottom") },
          { id: "system.hardRefresh", type: "button", label: t("Hard refresh website") },
        ],
      },
    ];
  }, [me]);

  const send = React.useCallback((message) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) return false;
    socketRef.current.send(JSON.stringify(message));
    return true;
  }, []);

  const publishContext = React.useCallback(() => {
    if (!controllerConnectedRef.current) return;
    const page = pageRef.current?.getConfig?.() || {};
    const contextId = `${location.pathname}${location.search}:${page.id || "generic"}`;
    sequenceRef.current += 1;
    send({
      v: 1,
      type: "context.snapshot",
      contextId,
      sequence: sequenceRef.current,
      page: {
        id: page.id || "generic",
        title: page.title || document.title || t("Pocket Play"),
        subtitle: page.subtitle || t("Website controls"),
        path: location.pathname,
      },
      state: page.state || {},
      // Keep navigation and scrolling reachable before page-specific controls on phones.
      sections: [...navigationControls, ...(page.sections || [])],
    });
  }, [location.pathname, location.search, navigationControls, send]);

  const publishRef = React.useRef(publishContext);
  publishRef.current = publishContext;

  const sendResult = React.useCallback((request, ok, detail = "") => {
    send({
      v: 1,
      type: "action.result",
      messageId: request.messageId,
      contextId: request.contextId,
      ok,
      detail,
    });
  }, [send]);

  const handleAction = React.useCallback(async (message) => {
    const page = pageRef.current?.getConfig?.() || {};
    const currentContextId = `${location.pathname}${location.search}:${page.id || "generic"}`;
    if (message.contextId !== currentContextId) {
      sendResult(message, false, t("The website changed. Controls were refreshed."));
      publishRef.current();
      return;
    }
    const action = globalActions[message.action] || page.actions?.[message.action];
    if (!action) {
      sendResult(message, false, t("That control is not available now."));
      return;
    }
    try {
      await action(message.payload || {});
      sendResult(message, true);
      window.setTimeout(() => publishRef.current(), 0);
    } catch (actionError) {
      sendResult(message, false, actionError?.message || t("The action failed."));
    }
  }, [globalActions, location.pathname, location.search, sendResult]);

  const actionRef = React.useRef(handleAction);
  actionRef.current = handleAction;

  const clearControllerReconnectTimer = React.useCallback(() => {
    window.clearTimeout(controllerReconnectTimerRef.current);
    controllerReconnectTimerRef.current = null;
  }, []);

  const scheduleControllerPairingReset = React.useCallback(() => {
    clearControllerReconnectTimer();
    if (!sessionRef.current?.controllerClaimed) return;
    controllerReconnectTimerRef.current = window.setTimeout(() => {
      controllerReconnectTimerRef.current = null;
      if (closedRef.current || controllerConnectedRef.current || !sessionRef.current?.controllerClaimed) return;
      setDialogOpen(true);
      resetPairingRef.current?.();
    }, CONTROLLER_RECONNECT_GRACE_MS);
  }, [clearControllerReconnectTimer]);

  const connectSocket = React.useCallback((ticket) => {
    const previous = socketRef.current;
    if (previous) {
      previous.onclose = null;
      previous.close();
    }
    const socket = new WebSocket(remoteSocketUrl(ticket));
    socketRef.current = socket;
    setStatus("connecting");
    socket.onopen = () => {
      setError("");
      setStatus(controllerConnectedRef.current ? "connected" : "waiting");
    };
    socket.onmessage = (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch { return; }
      if (message.type === "connection.ready") {
        const peerConnected = Boolean(message.peerConnected);
        if (peerConnected) clearControllerReconnectTimer();
        else if (sessionRef.current?.controllerClaimed) scheduleControllerPairingReset();
        controllerConnectedRef.current = peerConnected;
        setControllerConnected(peerConnected);
        setStatus(peerConnected ? "connected" : (sessionRef.current?.controllerClaimed ? "disconnected" : "waiting"));
        if (peerConnected && sessionRef.current) {
          const stored = { ...sessionRef.current, controllerClaimed: true, controllerConnected: true };
          sessionRef.current = stored;
          setSession(stored);
          storeHost(stored);
        } else if (sessionRef.current) {
          const stored = { ...sessionRef.current, controllerConnected: false };
          sessionRef.current = stored;
          setSession(stored);
          storeHost(stored);
        }
        publishRef.current();
      }
      if (message.type === "presence" && message.role === "controller") {
        if (message.connected) clearControllerReconnectTimer();
        else scheduleControllerPairingReset();
        controllerConnectedRef.current = Boolean(message.connected);
        setControllerConnected(Boolean(message.connected));
        setStatus(message.connected ? "connected" : "disconnected");
        if (message.connected && sessionRef.current) {
          const stored = { ...sessionRef.current, controllerClaimed: true, controllerConnected: true };
          sessionRef.current = stored;
          setSession(stored);
          storeHost(stored);
        } else if (sessionRef.current) {
          const stored = { ...sessionRef.current, controllerConnected: false };
          sessionRef.current = stored;
          setSession(stored);
          storeHost(stored);
        }
        if (message.connected) window.setTimeout(() => publishRef.current(), 0);
      }
      if (message.type === "action") actionRef.current(message);
      if (message.type === "ping") send({ v: 1, type: "pong", messageId: message.messageId || messageId() });
    };
    socket.onerror = () => setError(t("Could not connect to the remote-control service."));
    socket.onclose = (event) => {
      if (socketRef.current !== socket || closedRef.current) return;
      socketRef.current = null;
      setStatus(event.code === 4411 ? "ended" : "reconnecting");
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = window.setTimeout(async () => {
        const current = sessionRef.current;
        if (!current || closedRef.current) return;
        try {
          const result = await reconnectRemoteSession(current.sessionId, current.hostReconnectToken);
          connectSocket(result.ticket);
        } catch (reconnectError) {
          setError(reconnectError?.response?.data?.detail || t("Remote reconnection failed."));
          setStatus("ended");
        }
      }, 2000);
    };
  }, [clearControllerReconnectTimer, scheduleControllerPairingReset, send]);

  React.useEffect(() => {
    const stored = readStoredHost();
    if (!stored?.sessionId || !stored?.hostReconnectToken) return undefined;
    closedRef.current = false;
    setSession(stored);
    setControllerConnected(Boolean(stored.controllerConnected));
    reconnectRemoteSession(stored.sessionId, stored.hostReconnectToken)
      .then((result) => {
        const restored = { ...stored, controllerClaimed: Boolean(result.controllerClaimed) };
        sessionRef.current = restored;
        setSession(restored);
        storeHost(restored);
        setStatus(result.controllerClaimed ? "disconnected" : "waiting");
        connectSocket(result.ticket);
      })
      .catch(() => {
        storeHost(null);
        setSession(null);
        setStatus("idle");
      });
    return undefined;
  }, [connectSocket]);

  React.useEffect(() => {
    publishContext();
  }, [publishContext]);

  React.useEffect(() => () => {
    closedRef.current = true;
    window.clearTimeout(reconnectTimerRef.current);
    clearControllerReconnectTimer();
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.close();
    }
  }, [clearControllerReconnectTimer]);

  const registerPage = React.useCallback((id, getConfig) => {
    const registration = { id, getConfig };
    pageRef.current = registration;
    window.setTimeout(() => publishRef.current(), 0);
    return () => {
      if (pageRef.current === registration) pageRef.current = null;
    };
  }, []);

  const startPairing = React.useCallback(async () => {
    setDialogOpen(true);
    if (sessionRef.current) return;
    setStatus("creating");
    setError("");
    closedRef.current = false;
    try {
      const created = await createRemoteSession();
      const stored = {
        sessionId: created.sessionId,
        hostReconnectToken: created.hostReconnectToken,
        pairUrl: created.pairUrl,
        pairExpiresAt: created.pairExpiresAt,
        controllerConnected: false,
        controllerClaimed: false,
      };
      sessionRef.current = stored;
      setSession(stored);
      storeHost(stored);
      setStatus("waiting");
      connectSocket(created.hostTicket);
    } catch (createError) {
      setStatus("error");
      setError(createError?.response?.data?.detail || t("Could not create a remote session."));
    }
  }, [connectSocket]);

  const resetPairing = React.useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return startPairing();
    clearControllerReconnectTimer();
    setDialogOpen(true);
    setStatus("creating");
    setError("");
    try {
      const reset = await resetRemoteSession(current.sessionId, current.hostReconnectToken);
      const stored = {
        ...current,
        pairUrl: reset.pairUrl,
        pairExpiresAt: reset.pairExpiresAt,
        controllerConnected: false,
        controllerClaimed: false,
      };
      controllerConnectedRef.current = false;
      setControllerConnected(false);
      sessionRef.current = stored;
      setSession(stored);
      storeHost(stored);
      setStatus("waiting");
      connectSocket(reset.hostTicket);
    } catch (resetError) {
      setStatus("error");
      setError(resetError?.response?.data?.detail || t("Could not reset pairing."));
    }
  }, [clearControllerReconnectTimer, connectSocket, startPairing]);

  resetPairingRef.current = resetPairing;

  const endPairing = React.useCallback(async () => {
    const current = sessionRef.current;
    closedRef.current = true;
    window.clearTimeout(reconnectTimerRef.current);
    clearControllerReconnectTimer();
    socketRef.current?.close();
    socketRef.current = null;
    if (current) {
      try { await endRemoteSession(current.sessionId, current.hostReconnectToken); } catch { /* best effort */ }
    }
    sessionRef.current = null;
    controllerConnectedRef.current = false;
    setSession(null);
    setControllerConnected(false);
    setStatus("idle");
    setDialogOpen(false);
    setError("");
    storeHost(null);
  }, [clearControllerReconnectTimer]);

  const value = React.useMemo(() => ({
    session,
    status,
    controllerConnected,
    remoteActive: controllerConnected && status === "connected",
    dialogOpen,
    error,
    setDialogOpen,
    startPairing,
    resetPairing,
    endPairing,
    registerPage,
    publishContext,
  }), [controllerConnected, dialogOpen, endPairing, error, publishContext, registerPage, resetPairing, session, startPairing, status]);

  return <RemoteControlContext.Provider value={value}>{children}</RemoteControlContext.Provider>;
}

export function useRemoteControl() {
  const value = React.useContext(RemoteControlContext);
  if (!value) throw new Error("useRemoteControl must be used inside RemoteControlProvider");
  return value;
}

export function useRemotePage(config) {
  const { registerPage, publishContext } = useRemoteControl();
  const configRef = React.useRef(config);
  configRef.current = config;
  const revision = JSON.stringify({ state: config.state || {}, sections: config.sections || [] });

  React.useEffect(
    () => config.enabled === false ? undefined : registerPage(config.id, () => configRef.current),
    [config.enabled, config.id, registerPage]
  );
  React.useEffect(() => {
    publishContext();
  }, [publishContext, revision]);
}
